import type { OptionsType } from '@shared/components';
import { CancelReasonType } from '@shared/services/appointment/form/types/appointmentForm.type';
import { MESSAGE_ACTIONS_KEYS } from '@shared/utils/bridge';

import { RequestFormType } from '@features/Appointment/AppointmentMakeNew/types/makeNew';

export const REASON_CANCELLATION = [
  {
    label: 'I am well already',
    value: CancelReasonType.reasonWell,
  },
  {
    label: 'No one is able to accompany me',
    value: CancelReasonType.reasonNoCompany,
  },
  {
    label: 'Schedule conflicts with my work/family commitments',
    value: CancelReasonType.reasonScheduleConflict,
  },
  {
    label: 'I am seeking treatment elsewhere',
    value: CancelReasonType.reasonTreatmentElsewhere,
  },
  {
    label: 'Others',
    value: CancelReasonType.reasonOthers,
  },
];

export const I_WISH_QUESTION = [
  {
    label: 'Change Appointment',
    value: RequestFormType.Change,
  },
  {
    label: 'Cancel Appointment',
    value: RequestFormType.Cancel,
  },
];

export const HB_I_WISH_QUESTION = [
  {
    label: 'Make Appointment',
    value: RequestFormType.Make,
  },
  {
    label: 'Change Appointment',
    value: RequestFormType.Change,
  },
  {
    label: 'Cancel Appointment',
    value: RequestFormType.Cancel,
  },
];

export const REFERRED_BY_GP_VALUE = 'gp';
export const REFERRED_BY_SPECIALIST_VALUE = 'specialist';
export const REFERRED_BY_MYSELF_VALUE = 'myself';

export const REFERRED_BY_OPTION = [
  {
    label: 'GP / Family Doctor',
    value: REFERRED_BY_GP_VALUE,
  },
  {
    label: 'Specialist',
    value: REFERRED_BY_SPECIALIST_VALUE,
  },
  {
    label: 'Myself',
    value: REFERRED_BY_MYSELF_VALUE,
  },
];

export const REFERRED_BY_OPTION_WITH_DESC = REFERRED_BY_OPTION.map((option) => ({
  ...option,
  desc: option.value === REFERRED_BY_MYSELF_VALUE ? 'No referral from Polyclinic / CHAS GP' : '',
}));

export const HB_REFERRED_BY_VALUES = {
  GPSP: 'gpsp',
  GPSPCHAS: 'gpspchas',
  Myself: 'myself',
  Polyclinic: 'polyclinic',
  SAF: 'saf',
};

export const HB_PATIENT_TYPE_VALUES = {
  OverseasPatient: 'Overseas Patient',
  PermanentResident: 'Permanent Resident',
  Singaporean: 'Singaporean',
  SingaporeForeignResident: 'Singapore Foreign Resident/ Long Term Pass Holder',
};

export const HB_REFERRED_BY_OPTIONS: OptionsType[] = [
  {
    desc: `Private appointment, <a target="blank" data-navigation="${MESSAGE_ACTIONS_KEYS.externalLink}" href="https://www.singhealth.com.sg/patient-care/patient-visitor-info/fees-charges" className="text-theme-600">non-subsidised</a>`,
    isUseHtmlForDesc: true,
    label: 'Myself',
    value: HB_REFERRED_BY_VALUES.Myself,
  },
  {
    desc: `Private patient, <a target="blank" data-navigation="${MESSAGE_ACTIONS_KEYS.externalLink}" href="https://www.singhealth.com.sg/patient-care/patient-visitor-info/fees-charges" className="text-theme-600">non-subsidised</a>`,
    isUseHtmlForDesc: true,
    label: 'GP/Family Doctor/Specialist',
    value: HB_REFERRED_BY_VALUES.GPSP,
  },
  {
    desc: '-WITH CHAS REFERRAL SUBSIDY FORM To qualify as a CHAS subsidised referral, patient must present a VALID CHAS CARD and COMPLETED CHAS REFERRAL FORM by referring GP/Clinic. Please include as an attachment in this form. <br/> <strong>Impt</strong>: Green CHAS card holder referred from CHAS Dental Clinics are not eligible for subsidies.',
    isUseHtmlForDesc: true,
    label: 'GP/Family Doctor/Specialist',
    value: HB_REFERRED_BY_VALUES.GPSPCHAS,
  },
  {
    desc: 'Subsidised, requires referral letter',
    label: 'Polyclinics',
    value: HB_REFERRED_BY_VALUES.Polyclinic,
  },
  {
    desc: 'Subsidised, requires referral letter <br/> *Not applicable to National Heart Centre',
    isUseHtmlForDesc: true,
    label: 'Singapore Armed Forces (SAF)',
    value: HB_REFERRED_BY_VALUES.SAF,
  },
];

export const HB_PATIENT_TYPE_OPTIONS: OptionsType[] = [
  {
    label: 'Singaporean',
    value: HB_PATIENT_TYPE_VALUES.Singaporean,
  },
  {
    label: 'Permanent Resident',
    value: HB_PATIENT_TYPE_VALUES.PermanentResident,
  },
  {
    desc: '(e.g. Employment pass, work permit, student pass)',
    label: 'Singapore Foreign Resident/ Long Term Pass Holder',
    value: HB_PATIENT_TYPE_VALUES.SingaporeForeignResident,
  },
  {
    label: 'Overseas Patient (Not residing in Singapore)',
    value: HB_PATIENT_TYPE_VALUES.OverseasPatient,
  },
];

export const HB_GENDER_OPTIONS: OptionsType[] = [
  {
    label: 'Male',
    value: 'Male',
  },
  {
    label: 'Female',
    value: 'Female',
  },
];

export const IMAGE_UPLOAD_MAX_SIZE = 2000 * 1024; // 2MB

export const RELATIONSHIP_TO_PATIENT_OPTIONS = [
  {
    label: 'Father/Mother',
    value: 'Father/Mother',
  },
  {
    label: 'Parent In Law',
    value: 'Parent In Law',
  },
  {
    label: 'Spouse',
    value: 'Spouse',
  },
  {
    label: 'Son/Daughter',
    value: 'Son/Daughter',
  },
  {
    label: 'Brother/Sister',
    value: 'Brother/Sister',
  },
  {
    label: 'Grandparent',
    value: 'Grandparent',
  },
  {
    label: 'Grandparent In Law',
    value: 'Grandparent In Law',
  },
  {
    label: 'Grandchild',
    value: 'Grandchild',
  },
  {
    label: 'Uncle/Aunt',
    value: 'Uncle/Aunt',
  },
  {
    label: 'Other Relatives',
    value: 'Other Relatives',
  },
  {
    label: 'Colleague',
    value: 'Colleague',
  },
  {
    label: 'Friend',
    value: 'Friend',
  },
  {
    label: 'Other Non-Relatives',
    value: 'Other Non-Relatives',
  },
];

export const APPOINTMENT_REQUEST_TYPE = {
  NEW: 'new',
  FOLLOW_UP: 'followup',
};

export const APPOINTMENT_REQUEST_TYPE_OPTIONS = [
  {
    value: APPOINTMENT_REQUEST_TYPE.NEW,
    label: 'New appointment',
  },
  {
    value: APPOINTMENT_REQUEST_TYPE.FOLLOW_UP,
    label: 'Follow-up appointment',
  },
];

export const APPOINTMENT_PREFERENCE_OPTIONS = [
  {
    value: 'morning',
    label: 'Morning',
  },
  {
    value: 'afternoon',
    label: 'Afternoon',
  },
  {
    value: 'no-preference',
    label: 'No Preference',
  },
];
export const APPOINTMENT_NUMBEROFWORKINGDAYS_CONFIG = 'Appointment.Configuration.NumberOfWorkingDays';
